import React, { useEffect, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  SimpleGrid,
  Stack,
  Text,
} from '@chakra-ui/react';
import { CheckCircle } from 'phosphor-react';

import {
  CRITERIO_DEFAULT,
  CRITERIO_DEFAULTS_AVANCADO,
  CriterioModo,
  ICriterioFechamento,
} from '../../../../../hooks/useCriterioFechamento';

// ============================================================================
// Catalogo de modos (apresentacao)
// ============================================================================

interface ModoOption {
  modo: CriterioModo;
  titulo: string;
  resumo: string;
  badge: string;
  badgeColor: string;
}

const MODOS: ModoOption[] = [
  {
    modo: 'mensal',
    titulo: 'Mensal',
    resumo:
      'Última competência fechada. Ignora mês atual (em curso) e mês anterior (em fechamento) — como uma fatura.',
    badge: 'Estrito',
    badgeColor: 'red',
  },
  {
    modo: 'trimestral',
    titulo: 'Trimestral',
    resumo: 'Em dia = fechou o trimestre passado completo. O trimestre atual é ignorado.',
    badge: 'Intermediário',
    badgeColor: 'orange',
  },
  {
    modo: 'exercicio',
    titulo: 'Por exercício (ano fiscal)',
    resumo: 'Em dia = fechou pelo menos o exercício passado (desde 1º jan).',
    badge: 'Tolerante',
    badgeColor: 'green',
  },
  {
    modo: 'avancado',
    titulo: 'Avançado',
    resumo: 'Dias de tolerância por regime, contados a partir do fim do mês passado.',
    badge: 'Personalizado',
    badgeColor: 'purple',
  },
];

// ============================================================================
// Card selecionavel de modo
// ============================================================================

interface ModoCardProps {
  opt: ModoOption;
  selecionado: boolean;
  onSelect: () => void;
}

const ModoCard = ({ opt, selecionado, onSelect }: ModoCardProps) => (
  <Box
    as="button"
    type="button"
    onClick={onSelect}
    p={4}
    borderRadius="xl"
    border="2px solid"
    borderColor={selecionado ? '#1B88F5' : 'rgba(15, 23, 42, 0.08)'}
    bg={selecionado ? 'rgba(27, 136, 245, 0.06)' : 'white'}
    textAlign="left"
    transition="all 0.18s ease"
    _hover={!selecionado ? { borderColor: 'rgba(27, 136, 245, 0.35)' } : undefined}
    position="relative"
  >
    {selecionado && (
      <Box position="absolute" top={3} right={3} color="#1B88F5">
        <CheckCircle size={20} weight="fill" />
      </Box>
    )}
    <HStack mb={2} spacing={2}>
      <Badge colorScheme={opt.badgeColor} fontSize="10px" borderRadius="full" px={2}>
        {opt.badge}
      </Badge>
    </HStack>
    <Text fontSize="sm" fontWeight="700" color="#0F172A" mb={1}>
      {opt.titulo}
    </Text>
    <Text fontSize="xs" color="#71717A" lineHeight="1.45">
      {opt.resumo}
    </Text>
  </Box>
);

// ============================================================================
// Inputs do modo avancado
// ============================================================================

interface DiasInputProps {
  label: string;
  hint: string;
  value: number;
  defaultValue: number;
  // eslint-disable-next-line no-unused-vars
  onChange: (v: number) => void;
}

const DiasInput = ({ label, hint, value, defaultValue, onChange }: DiasInputProps) => (
  <FormControl>
    <Flex align="center" justify="space-between" mb={1}>
      <FormLabel fontSize="xs" fontWeight="600" color="#0F172A" m={0}>
        {label}
      </FormLabel>
      <Text fontSize="10px" color="#94A3B8">
        padrão: {defaultValue}d
      </Text>
    </Flex>
    <NumberInput
      size="sm"
      value={value}
      min={1}
      max={3650}
      onChange={(_, n) => onChange(Number.isNaN(n) ? defaultValue : n)}
    >
      <NumberInputField borderRadius="lg" />
      <NumberInputStepper>
        <NumberIncrementStepper />
        <NumberDecrementStepper />
      </NumberInputStepper>
    </NumberInput>
    <FormHelperText fontSize="10px" color="#94A3B8" mt={1}>
      {hint}
    </FormHelperText>
  </FormControl>
);

// ============================================================================
// Modal principal
// ============================================================================

interface CriterioCalculoModalProps {
  isOpen: boolean;
  onClose: () => void;
  current: ICriterioFechamento;
  // eslint-disable-next-line no-unused-vars
  onApply: (c: ICriterioFechamento) => void;
}

export const CriterioCalculoModal = ({
  isOpen,
  onClose,
  current,
  onApply,
}: CriterioCalculoModalProps) => {
  const [draft, setDraft] = useState<ICriterioFechamento>(current);
  const prevIsOpen = useRef(false);

  // Sincroniza apenas na transicao fechado -> aberto. Se `current` mudasse
  // com o modal aberto (raro, mas possivel), o rascunho do usuario seria
  // sobrescrito sem aviso. `current` esta intencionalmente fora das deps.
  useEffect(() => {
    if (isOpen && !prevIsOpen.current) setDraft(current);
    prevIsOpen.current = isOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleApply = () => {
    onApply(draft);
    onClose();
  };

  const handleResetDefaults = () => {
    setDraft(CRITERIO_DEFAULT);
  };

  const handleResetAvancado = () => {
    setDraft(prev => ({
      ...prev,
      diasLr: CRITERIO_DEFAULTS_AVANCADO.diasLr,
      diasLp: CRITERIO_DEFAULTS_AVANCADO.diasLp,
      diasSn: CRITERIO_DEFAULTS_AVANCADO.diasSn,
      diasMei: CRITERIO_DEFAULTS_AVANCADO.diasMei,
      diasOutros: CRITERIO_DEFAULTS_AVANCADO.diasOutros,
    }));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" isCentered scrollBehavior="inside">
      <ModalOverlay backdropFilter="blur(8px)" bg="rgba(15, 23, 42, 0.4)" />
      <ModalContent borderRadius="2xl" overflow="hidden">
        <ModalHeader borderBottom="1px solid" borderColor="rgba(15, 23, 42, 0.06)" pb={4}>
          <Text fontSize="lg" fontWeight="700" color="#0F172A" letterSpacing="-0.01em">
            Critério de cálculo — Empresas em dia
          </Text>
          <Text fontSize="xs" color="#71717A" fontWeight="normal" mt={1}>
            Define como uma empresa é considerada em dia com a contabilidade. Aplica-se só a
            clientes ativos — inativos continuam sendo avaliados pela data de saída.
          </Text>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody py={5}>
          <Stack spacing={5}>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
              {MODOS.map(opt => (
                <ModoCard
                  key={opt.modo}
                  opt={opt}
                  selecionado={draft.modo === opt.modo}
                  onSelect={() => setDraft(prev => ({ ...prev, modo: opt.modo }))}
                />
              ))}
            </SimpleGrid>

            {draft.modo === 'avancado' && (
              <Box
                p={4}
                borderRadius="xl"
                bg="rgba(15, 23, 42, 0.03)"
                border="1px solid"
                borderColor="rgba(15, 23, 42, 0.06)"
              >
                <Flex justify="space-between" align="center" mb={3}>
                  <Text fontSize="sm" fontWeight="700" color="#0F172A">
                    Tolerância por regime (em dias)
                  </Text>
                  <Button
                    size="xs"
                    variant="ghost"
                    colorScheme="blue"
                    onClick={handleResetAvancado}
                  >
                    Restaurar padrões
                  </Button>
                </Flex>

                <SimpleGrid columns={{ base: 2, md: 3 }} spacing={4}>
                  <DiasInput
                    label="Lucro Real"
                    hint="Apuração mensal/trimestral exigida."
                    value={draft.diasLr ?? CRITERIO_DEFAULTS_AVANCADO.diasLr}
                    defaultValue={CRITERIO_DEFAULTS_AVANCADO.diasLr}
                    onChange={n => setDraft(prev => ({ ...prev, diasLr: n }))}
                  />
                  <DiasInput
                    label="Lucro Presumido"
                    hint="Apuração trimestral; tolerância maior."
                    value={draft.diasLp ?? CRITERIO_DEFAULTS_AVANCADO.diasLp}
                    defaultValue={CRITERIO_DEFAULTS_AVANCADO.diasLp}
                    onChange={n => setDraft(prev => ({ ...prev, diasLp: n }))}
                  />
                  <DiasInput
                    label="Simples Nacional"
                    hint="DASN anual; menos pressão mensal."
                    value={draft.diasSn ?? CRITERIO_DEFAULTS_AVANCADO.diasSn}
                    defaultValue={CRITERIO_DEFAULTS_AVANCADO.diasSn}
                    onChange={n => setDraft(prev => ({ ...prev, diasSn: n }))}
                  />
                  <DiasInput
                    label="MEI"
                    hint="Sem contabilidade obrigatória regular."
                    value={draft.diasMei ?? CRITERIO_DEFAULTS_AVANCADO.diasMei}
                    defaultValue={CRITERIO_DEFAULTS_AVANCADO.diasMei}
                    onChange={n => setDraft(prev => ({ ...prev, diasMei: n }))}
                  />
                  <DiasInput
                    label="Outros"
                    hint="Domésticas, Imune/Isenta etc."
                    value={draft.diasOutros ?? CRITERIO_DEFAULTS_AVANCADO.diasOutros}
                    defaultValue={CRITERIO_DEFAULTS_AVANCADO.diasOutros}
                    onChange={n => setDraft(prev => ({ ...prev, diasOutros: n }))}
                  />
                </SimpleGrid>
              </Box>
            )}
          </Stack>
        </ModalBody>

        <ModalFooter borderTop="1px solid" borderColor="rgba(15, 23, 42, 0.06)" gap={2}>
          <Button variant="ghost" size="sm" onClick={handleResetDefaults}>
            Restaurar critério padrão
          </Button>
          <Box flex={1} />
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button colorScheme="blue" size="sm" onClick={handleApply}>
            Aplicar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
